/**
 * Review paragraphs — render a Chinese review paragraph block for one
 * disease × type subsection. Each paragraph is wrapped in a `<p>` whose
 * text runs through `compactCitationsInParagraph()` so citation ranges
 * like `[1-3]` collapse to `[1-3]` (i.e. the existing text already
 * uses brackets; the helper normalises whitespace and adjacent commas
 * inside them).
 *
 * Why a helper and not a regex strip?
 * ───────────────────────────────────
 * The LLM-produced Chinese reviews routinely emit strings like
 * `[1]、[2]、[3]` or `[1, 2, 3]` or `[1-3]`. The simplest UX fix is
 * to flatten whitespace and unify the comma separator; the helper
 * below does that without disturbing citations that already look
 * clean.
 */

import { el } from "../dom";

export interface ReviewParagraphsOptions {
  /** Paragraphs produced by the summarizer. Each becomes one `<p>`. */
  paragraphs: string[];
}

export function reviewParagraphs({ paragraphs }: ReviewParagraphsOptions): HTMLElement {
  const root = el("div", { class: "review-paragraphs" });
  if (!paragraphs || paragraphs.length === 0) {
    root.appendChild(
      el("p", { class: "muted review-paragraphs-empty" }, "（暂无综述段落）"),
    );
    return root;
  }
  for (const raw of paragraphs) {
    const text = compactCitationsInParagraph(raw);
    root.appendChild(el("p", { class: "review-paragraph" }, text));
  }
  return root;
}

/**
 * Normalise a single paragraph's citation markers. Only touches
 * whitespace and adjacent punctuation; no semantic parsing.
 */
export function compactCitationsInParagraph(text: string): string {
  if (!text) return "";
  return text
    .replace(/、\s+/gu, "、")
    .replace(/\s+、/gu, "、")
    .replace(/\[\s+/gu, "[")
    .replace(/\s+\]/gu, "]")
    .replace(/、\]/gu, "]")
    .replace(/\]\s*\[/gu, "][")
    .replace(/\[(\d+)\s*-\s*(\d+)\]/gu, "[$1-$2]")
    .trim();
}